import { useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon, PlayIcon } from "@hugeicons/core-free-icons"
import { cn } from "@/lib/utils"
import type { PlatformGuide } from "@/data/platform-guides"
import type { PlatformMedia } from "@/lib/types/platform"

/** Canonical player frame — keeps Webflow/Framer/etc. the same height on MacBook viewports. */
const INSTALL_MEDIA_ASPECT = "16 / 9"
/** Soft cap so square source files can't blow the fold (~168px at ~300px content width). */
const INSTALL_MEDIA_MAX_CLASS = "max-h-[168px]"

type PlatformInstallMediaProps = {
  guide: PlatformGuide
  className?: string
  /** When true, video starts collapsed behind "Watch walkthrough" (default for fold). */
  videoCollapsedByDefault?: boolean
}

function cloudflareStreamHost(customer: string): string {
  const trimmed = customer.replace(/^https?:\/\//i, "").replace(/\/+$/, "")
  if (trimmed.includes(".")) return trimmed
  return `${trimmed}.cloudflarestream.com`
}

function mediaSrc(media: PlatformMedia): string {
  if (media.type === "youtube") {
    return `https://www.youtube.com/embed/${media.id}?rel=0&modestbranding=1`
  }
  if (media.type === "loom") {
    return `https://www.loom.com/embed/${media.id}`
  }
  if (media.type === "cloudflare-stream") {
    const host = cloudflareStreamHost(media.customer)
    const poster =
      media.posterUrl ||
      `https://${host}/${media.id}/thumbnails/thumbnail.jpg?height=600`
    // Sources are 16:9, so letterboxing shouldn't show. Stream only accepts a
    // 6-digit hex here — `transparent` is ignored and the player stays black.
    return `https://${host}/${media.id}/iframe?poster=${encodeURIComponent(poster)}`
  }
  return media.url
}

function hasEmbeddableMedia(media: PlatformMedia | undefined): media is PlatformMedia {
  return Boolean(
    media &&
      (media.type === "youtube" ||
        media.type === "loom" ||
        media.type === "cloudflare-stream" ||
        media.type === "gif" ||
        media.type === "video"),
  )
}

/**
 * Walkthrough media for install guidance.
 * Always shows the animated step preview (all platforms). When a Stream/YouTube/
 * Loom/GIF is configured, a compact 16:9 player is available behind a toggle so
 * Copy code stays above the fold on laptop viewports.
 */
export function PlatformInstallMedia({
  guide,
  className,
  videoCollapsedByDefault = true,
}: PlatformInstallMediaProps) {
  const media = guide.media
  const canWatch = hasEmbeddableMedia(media)
  const [videoOpen, setVideoOpen] = useState(!videoCollapsedByDefault && canWatch)

  useEffect(() => {
    setVideoOpen(!videoCollapsedByDefault && canWatch)
  }, [guide.id, videoCollapsedByDefault, canWatch])

  return (
    <div className={cn("space-y-2", className)}>
      <AnimatedInstallWalkthrough guide={guide} />

      {hasEmbeddableMedia(media) && (
        <div className="rounded-lg border border-border/40 overflow-hidden bg-muted/20">
          <button
            type="button"
            aria-expanded={videoOpen}
            onClick={() => setVideoOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
          >
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <HugeiconsIcon icon={PlayIcon} size={12} className="text-muted-foreground shrink-0" />
              <span className="text-[11px] font-medium text-foreground truncate">
                {media.title || `Watch ${guide.name} walkthrough`}
              </span>
            </span>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={12}
              aria-hidden
              className={cn(
                "text-muted-foreground shrink-0 transition-transform duration-200",
                videoOpen && "rotate-180",
              )}
            />
          </button>

          <AnimatePresence initial={false}>
            {videoOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                style={{ overflow: "hidden" }}
              >
                <EmbedPlayer media={media} title={media.title || `${guide.name} install walkthrough`} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}

function EmbedPlayer({ media, title }: { media: PlatformMedia; title: string }) {
  if (media.type === "gif") {
    return (
      <img
        src={media.url}
        alt={title}
        className={cn("w-full object-cover bg-muted/30 border-t border-border/30", INSTALL_MEDIA_MAX_CLASS)}
        style={{ aspectRatio: INSTALL_MEDIA_ASPECT }}
      />
    )
  }

  if (media.type === "video") {
    return (
      <video
        src={media.url}
        poster={media.posterUrl}
        controls
        playsInline
        className={cn("w-full object-cover bg-black/90 border-t border-border/30", INSTALL_MEDIA_MAX_CLASS)}
        style={{ aspectRatio: INSTALL_MEDIA_ASPECT }}
      />
    )
  }

  return (
    <div
      className={cn(
        "relative w-full bg-muted/40 border-t border-border/30 overflow-hidden",
        INSTALL_MEDIA_MAX_CLASS,
      )}
      style={{ aspectRatio: INSTALL_MEDIA_ASPECT }}
    >
      <iframe
        title={title}
        src={mediaSrc(media)}
        className="absolute inset-0 h-full w-full border-0"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
      />
    </div>
  )
}

function AnimatedInstallWalkthrough({
  guide,
  className,
}: {
  guide: PlatformGuide
  className?: string
}) {
  const frames = useMemo(
    () => (guide.shortSteps.length > 0 ? guide.shortSteps : guide.steps.map((s) => s.title)),
    [guide.shortSteps, guide.steps],
  )
  const [index, setIndex] = useState(0)
  const step = frames[index] ?? frames[0]

  useEffect(() => {
    setIndex(0)
    if (frames.length <= 1) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % frames.length)
    }, 2200)
    return () => window.clearInterval(id)
  }, [frames])

  if (!step) return null

  return (
    <div className={cn("rounded-lg border border-border/40 overflow-hidden bg-muted/15", className)}>
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border/30">
        <div className="flex items-center gap-1.5 min-w-0">
          <HugeiconsIcon icon={PlayIcon} size={12} className="text-muted-foreground shrink-0" />
          <p className="text-[11px] font-medium text-foreground truncate">
            {guide.name} install preview
          </p>
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
          {index + 1}/{frames.length}
        </span>
      </div>

      <div className="relative px-3 py-2.5 min-h-[88px]">
        <div className="relative rounded-md border border-border/40 bg-card/80 px-3 py-2.5">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="size-1.5 rounded-full bg-border/80" />
            <span className="size-1.5 rounded-full bg-border/80" />
            <span className="size-1.5 rounded-full bg-border/80" />
            <span className="ml-2 h-1.5 w-16 rounded-full bg-muted" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${guide.id}-${index}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22 }}
              className="space-y-1.5"
            >
              <div className="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-muted/40 px-2 py-0.5">
                <span className="text-xs font-semibold text-muted-foreground">
                  Step {index + 1}
                </span>
              </div>
              <p className="text-xs font-medium text-foreground leading-snug">{step}</p>
              <div
                aria-hidden
                className="h-7 rounded border border-dashed border-border/50 bg-muted/30 flex items-center px-2"
              >
                <span className="text-[11px] font-mono text-muted-foreground truncate select-none">
                  Paste your tracking snippet
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="relative mt-2 flex items-center justify-center gap-1.5">
          {frames.map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1 rounded-full transition-all duration-300",
                i === index ? "w-4 bg-foreground/70" : "w-1.5 bg-border/70",
              )}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
